import { UpgradeToMuseProDialogType, useAppContext } from '@/AppContext'
import { AudioEngineLocalStreamStats } from '@/core/AudioEngine'
import CaretRightIcon from '@/CustomIcons/CaretRightIcon'
import useAppNotificationActions from '@/functionalities/AppNotification/useAppNotificationActions'
import { useRoomContext } from '@/RoomContext'
import useRootSnackbar from '@/services/useRootSnackbar'
import { testDeviceAddedTrigger, testDeviceRemovedTrigger } from '@/services/useTriggerPromptsOnMediaSourcesUpdate'
import { Button } from '@/UIComponents2/Button'
import { AudioInputError, AudioInputSelection, AudioInputStreamInfo, AudioSourceInfo } from '@/UserContext'

const testAudioSourceInfo: AudioSourceInfo = {
    deviceId: '',
    groupId: '',
    kind: 'audioinput',
    label: '',
    nativeDeviceId: null,
    audioEngineDeviceId: null,
    deviceUid: null,
    displayLabel: '',
    defaultSampleRate: 0,
    maxInputChannels: 0,
    maxOutputChannels: 0,
    hostApiName: '',
    isSystemAudio: false,
    isMuseAudioShare: false,
    isMuseDAWBridge: false,
}
const testAudioInputSelection: AudioInputSelection = {
    deviceId: '',
    settings: {
        sampleRate: 0,
        bitDepth: 0,
        enableLocalLoopback: false,
        isVoiceQuality: false,
        isHighQuality: false,
        isAudioEngine: false,
        channelSelection: [],
        isCompressed: false,
        compressionBitrate: 0,
        receiverBufferSizeMs: 0,
        receiverBufferMaxDriftMs: 0,
        framesPerBuffer: 0,
        networkPacketDurationMs: 0,
    },
    source: {
        deviceId: '',
        groupId: '',
        kind: 'audioinput',
        label: '',
        nativeDeviceId: null,
        audioEngineDeviceId: null,
        deviceUid: null,
        displayLabel: 'Test Device',
        defaultSampleRate: 0,
        maxInputChannels: 0,
        maxOutputChannels: 0,
        hostApiName: '',
        isSystemAudio: false,
        isMuseAudioShare: false,
        isMuseDAWBridge: false,
        matchingLoopbackSource: undefined,
    },
}
const testAudioInputStreamInfo: AudioInputStreamInfo = {
    deviceId: '',
    groupId: '',
    nativeDeviceId: '',
    audioEngineDeviceId: '',
    audioEngineStreamUuid: '',

    label: 'Test Device',
    displayLabel: 'Test Device',

    isSystemAudio: false,
    isMuseAudioShare: false,
    isMuseDAWBridge: false,

    channelCount: 2,
    channelSelection: [[1, 2]],
    channelPackingMap: [1, 2],
    channelUnpackingMap: [1, 2],
} as unknown as AudioInputStreamInfo
const testAudioEngineLocalStreamStats: AudioEngineLocalStreamStats = {
    volume: [],
    measuredSampleRate: 48000,
    expectedSampleRate: 44100,
    unresponsiveTimeMs: 0,
    processingTimeUs: 0,
    sampleRateMismatchWarning: false,
    streamUnresponsiveWarning: false,
}
const testAudioInputError: AudioInputError = {
    message: 'Test message',
    debugMessage: 'Test debug message',
}
const upgradeToMuseProDialogTypes = [
    'default',
    'sessionDurationLimitRunningOut',
    'sessionIsBoosted',
    'upgradedToPro',
    'boost',
    'sessionIsFull:host',
    'sessionIsFull:caller',
    'sessionIsFull:guest',
    'premiumFeature:noDeviceCountLimit',
    'premiumFeature:noCollaboratorCountLimit',
    'premiumFeature:useProAudio',
    'premiumFeature:noChannelCountLimit',
]

export default function StateTriggersPage() {
    const [, appDispatch] = useAppContext()
    const [, roomDispatch] = useRoomContext()

    const { show: showSnackbar } = useRootSnackbar()

    const { showAppNotification } = useAppNotificationActions()

    return (
        <div className="flex flex-col gap-0 w-full items-start px-8">
            <h2 className="text-2xl mb-2">Open dialogs</h2>
            <div className="flex gap-2">
                <CaretRightIcon />
                <Button
                    variant="link"
                    onClick={() => {
                        appDispatch({
                            type: 'update_app_state',
                            value: { showTooManyHostedSessionsDialog: { isHost: true } },
                        })
                    }}
                >
                    TooManyHostedSessionsErrorModal
                </Button>
            </div>
            <div className="flex gap-2">
                <CaretRightIcon />
                <Button
                    variant="link"
                    onClick={() => {
                        appDispatch({
                            type: 'update_app_state',
                            value: {
                                showAudioEngineSampleRateMismatchDialog: {
                                    selection: testAudioInputSelection,
                                    stats: testAudioEngineLocalStreamStats,
                                },
                            },
                        })
                    }}
                >
                    AudioEngineSampleRateMismatchDialog
                </Button>
            </div>
            <div className="flex gap-2">
                <CaretRightIcon />
                <Button
                    variant="link"
                    onClick={() => {
                        appDispatch({
                            type: 'update_app_state',
                            value: {
                                showAudioEngineStreamUnresponsiveDialog: {
                                    selection: testAudioInputSelection,
                                    stats: testAudioEngineLocalStreamStats,
                                },
                            },
                        })
                    }}
                >
                    AudioEngineStreamUnresponsiveDialog
                </Button>
            </div>
            <div className="flex gap-2">
                <CaretRightIcon />
                <Button
                    variant="link"
                    onClick={() => {
                        appDispatch({
                            type: 'update_app_state',
                            value: {
                                showChooseVirtualBackgroundModal: true,
                            },
                        })
                    }}
                >
                    ChooseVirtualBackgroundModal
                </Button>
            </div>
            <div className="flex gap-2">
                <CaretRightIcon />
                <Button
                    variant="link"
                    onClick={() => {
                        appDispatch({
                            type: 'update_app_state',
                            value: {
                                showAudioInputDeviceResolutionErrorDialog: {
                                    message: testAudioInputError.message,
                                    debugMessage: testAudioInputError.debugMessage,
                                },
                            },
                        })
                    }}
                >
                    AudioInputDeviceResolutionErrorDialog
                </Button>
            </div>
            <div className="flex gap-2">
                <CaretRightIcon />
                <Button
                    variant="link"
                    onClick={() => {
                        appDispatch({
                            type: 'update_app_state',
                            value: {
                                showVbAudioCableAutoDeselectionDialog: {
                                    deviceToDeselect: testAudioInputStreamInfo,
                                    selectedDevice: testAudioSourceInfo,
                                },
                            },
                        })
                    }}
                >
                    VbAudioCableAutoDeselectionDialog
                </Button>
            </div>
            <div className="flex gap-2">
                <CaretRightIcon />
                <Button
                    variant="link"
                    onClick={() => {
                        appDispatch({
                            type: 'update_app_state',
                            value: {
                                screenRecordingConversionProgress: {
                                    directory: 'directory',
                                    fileName: 'fileName',
                                    fullPath: 'fullPath',
                                    progress: 69,
                                },
                            },
                        })
                    }}
                >
                    ScreenRecordingSavingDialog
                </Button>
            </div>
            <div className="flex gap-2">
                <CaretRightIcon />
                <Button
                    variant="link"
                    onClick={() => {
                        appDispatch({
                            type: 'update_app_state',
                            value: {
                                showFreeTrialInfoDialog: true,
                            },
                        })
                    }}
                >
                    FreeTrialInfoDialog
                </Button>
            </div>

            <div className="flex gap-2">
                <CaretRightIcon />
                <Button
                    variant="link"
                    className="text-start"
                    onClick={() => {
                        appDispatch({
                            type: 'update_app_state',
                            value: {
                                showSessionIsBoostedDialog: true,
                            },
                        })
                    }}
                >
                    showSessionIsBoostedDialog
                </Button>
            </div>
            <div className="flex gap-2">
                <CaretRightIcon />
                <Button
                    variant="link"
                    className="text-start"
                    onClick={() => {
                        appDispatch({
                            type: 'update_app_state',
                            value: {
                                showAccountIsUpgradedDialog: true,
                            },
                        })
                    }}
                >
                    showAccountIsUpgradedDialog
                </Button>
            </div>
            <div className="flex gap-2">
                <CaretRightIcon />
                <Button
                    variant="link"
                    className="text-start"
                    onClick={() => {
                        appDispatch({
                            type: 'new_message_dialog',
                            value: {
                                id: 'test_dialog',
                                text: 'test message',
                                negativeButtonDoNotShowAgain: true,
                            },
                        })
                    }}
                >
                    newMessageDialog
                </Button>
            </div>
            <div className="flex gap-2 items-center">
                <CaretRightIcon />
                <div>showUpgradeToMuseProDialog</div>
            </div>
            {upgradeToMuseProDialogTypes.map((type) => (
                <div className="flex gap-2 ml-6">
                    <CaretRightIcon />
                    <Button
                        variant="link"
                        className="text-start"
                        onClick={() => {
                            appDispatch({
                                type: 'update_app_state',
                                value: {
                                    showUpgradeToMuseProDialog: {
                                        type: type as UpgradeToMuseProDialogType,
                                        username: 'testuser',
                                    },
                                },
                            })
                        }}
                    >
                        {type}
                    </Button>
                </div>
            ))}
            <div className="flex gap-2">
                <CaretRightIcon />
                <Button
                    variant="link"
                    onClick={() => {
                        roomDispatch({
                            type: 'update_room_context',
                            value: {
                                isRoomAppUpgradeRequiredDialogOpen: {
                                    type: 'outdated_room',
                                    username: 'testuser',
                                },
                            },
                        })
                    }}
                >
                    RoomAppUpgradeRequiredDialog (outdated_room)
                </Button>
            </div>
            <div className="flex gap-2">
                <CaretRightIcon />
                <Button
                    variant="link"
                    onClick={() => {
                        roomDispatch({
                            type: 'update_room_context',
                            value: {
                                isRoomAppUpgradeRequiredDialogOpen: {
                                    type: 'outdated_peer',
                                    username: 'testuser',
                                },
                            },
                        })
                    }}
                >
                    RoomAppUpgradeRequiredDialog (outdated_peer)
                </Button>
            </div>

            <div className="flex gap-2">
                <CaretRightIcon />
                <Button
                    variant="link"
                    onClick={() => {
                        roomDispatch({
                            type: 'update_room_context',
                            value: {
                                isRemoteDesktopControlRequestPromptOpen: {
                                    reqId: 1,
                                    fromSessionId: 'fromSessionId',
                                    fromUsername: 'fromUsername',
                                },
                            },
                        })
                    }}
                >
                    RemoteDesktopControlPermisionDecisionPopup
                </Button>
            </div>

            <div className="flex gap-2">
                <CaretRightIcon />
                <Button
                    variant="link"
                    onClick={() => {
                        testDeviceRemovedTrigger.trigger()
                    }}
                >
                    MediaDeviceRemovedPrompt
                </Button>
            </div>
            <div className="flex gap-2">
                <CaretRightIcon />
                <Button
                    variant="link"
                    onClick={() => {
                        testDeviceAddedTrigger.trigger()
                    }}
                >
                    NewMediaDevicePrompt
                </Button>
            </div>

            <h2 className="text-2xl my-2">Others</h2>
            <div className="flex gap-2">
                <CaretRightIcon />
                <Button
                    variant="link"
                    onClick={() => {
                        showSnackbar({
                            severity: 'error',
                            message: 'test error message',
                        })
                    }}
                >
                    Snackbar (error)
                </Button>
            </div>
            <div className="flex gap-2">
                <CaretRightIcon />
                <Button
                    variant="link"
                    onClick={() => {
                        showSnackbar({
                            severity: 'info',
                            message: 'test info message',
                        })
                    }}
                >
                    Snackbar (info)
                </Button>
            </div>
            <div className="flex gap-2">
                <CaretRightIcon />
                <Button
                    variant="link"
                    onClick={() => {
                        showSnackbar({
                            severity: 'warning',
                            message: 'test warning message',
                        })
                    }}
                >
                    Snackbar (warning)
                </Button>
            </div>
            <div className="flex gap-2">
                <CaretRightIcon />
                <Button
                    variant="link"
                    onClick={() => {
                        showSnackbar({
                            severity: 'success',
                            message: 'test success message',
                        })
                    }}
                >
                    Snackbar (success)
                </Button>
            </div>
            <div className="flex gap-2">
                <CaretRightIcon />
                <Button
                    variant="link"
                    onClick={() => {
                        showSnackbar({
                            severity: 'success',
                            message:
                                'test success message lorem ipsum dolor sit amet ' +
                                'lorem ipsum dolor sit amet ' +
                                'lorem ipsum dolor sit amet ' +
                                'lorem ipsum dolor sit amet ' +
                                'lorem ipsum dolor sit amet ',
                        })
                    }}
                >
                    Snackbar (success) (long message)
                </Button>
            </div>
            <div className="flex gap-2">
                <CaretRightIcon />
                <Button
                    variant="link"
                    onClick={() => {
                        showSnackbar({
                            severity: 'success',
                            message: 'test success message',
                            duration: 99999,
                        })
                    }}
                >
                    Snackbar (success) (long duration)
                </Button>
            </div>
            <div className="flex gap-2">
                <CaretRightIcon />
                <Button
                    variant="link"
                    onClick={() => {
                        showAppNotification({
                            title: 'Title',
                            body: 'Message',
                        })
                    }}
                >
                    Native App Notification
                </Button>
            </div>
        </div>
    )
}
